import _ from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref, toRaw } from 'vue'

import type {
  ComfyWorkflowJSON,
  NodeId
} from '@/platform/workflow/validation/schemas/workflowSchema'
import type {
  ResultItem,
  StatusWsMessageStatus,
  TaskItem,
  TaskOutput,
  TaskPrompt,
  TaskStatus,
  TaskType
} from '@/schemas/apiSchema'
import { api } from '@/scripts/api'
import type { ComfyApp } from '@/scripts/app'
import { useExtensionService } from '@/services/extensionService'
import { useAuthStore } from '@/stores/authStore'
import { useNodeOutputStore } from '@/stores/imagePreviewStore'

// Task type used in the API.
type APITaskType = 'queue' | 'history'

export enum TaskItemDisplayStatus {
  Running = 'Running',
  Pending = 'Pending',
  Completed = 'Completed',
  Failed = 'Failed',
  Cancelled = 'Cancelled'
}

export class ResultItemImpl {
  filename: string
  subfolder: string
  type: string

  nodeId: NodeId
  // 'audio' | 'images' | ...
  mediaType: string

  // VHS output specific fields
  format?: string
  frame_rate?: number

  constructor(obj: Record<string, any>) {
    this.filename = obj.filename ?? ''
    this.subfolder = obj.subfolder ?? ''
    this.type = obj.type ?? ''

    this.nodeId = obj.nodeId
    this.mediaType = obj.mediaType

    this.format = obj.format
    this.frame_rate = obj.frame_rate
  }

  get urlParams(): URLSearchParams {
    const params = new URLSearchParams()
    params.set('filename', this.filename)
    params.set('type', this.type)
    params.set('subfolder', this.subfolder)

    if (this.format) {
      params.set('format', this.format)
    }
    if (this.frame_rate) {
      params.set('frame_rate', this.frame_rate.toString())
    }
    return params
  }

  /**
   * VHS advanced preview URL. `/viewvideo` endpoint is provided by VHS node.
   *
   * `/viewvideo` always returns a webm file.
   */
  get vhsAdvancedPreviewUrl(): string {
    return api.apiURL('/viewvideo?' + this.urlParams)
  }

  get url(): string {
    // 如果 filename 是一个完整的 URL（如 obs_url），直接返回它
    if (
      this.filename &&
      (this.filename.startsWith('http://') ||
        this.filename.startsWith('https://'))
    ) {
      return this.filename
    }
    return api.apiURL('/view?' + this.urlParams)
  }

  get urlWithTimestamp(): string {
    return `${this.url}&t=${+new Date()}`
  }

  get isVhsFormat(): boolean {
    return !!this.format && !!this.frame_rate
  }

  get htmlVideoType(): string | undefined {
    if (this.isWebm) {
      return 'video/webm'
    }
    if (this.isMp4) {
      return 'video/mp4'
    }

    if (this.isVhsFormat) {
      if (this.format?.endsWith('webm')) {
        return 'video/webm'
      }
      if (this.format?.endsWith('mp4')) {
        return 'video/mp4'
      }
    }
    return undefined
  }

  get htmlAudioType(): string | undefined {
    if (this.isMp3) {
      return 'audio/mpeg'
    }
    if (this.isWav) {
      return 'audio/wav'
    }
    if (this.isOgg) {
      return 'audio/ogg'
    }
    if (this.isFlac) {
      return 'audio/flac'
    }
    return undefined
  }

  get isGif(): boolean {
    return this.filename.endsWith('.gif')
  }

  get isWebp(): boolean {
    return this.filename.endsWith('.webp')
  }

  get isWebm(): boolean {
    return this.filename.endsWith('.webm')
  }

  get isMp4(): boolean {
    return this.filename.endsWith('.mp4')
  }

  get isVideoBySuffix(): boolean {
    return this.isWebm || this.isMp4
  }

  get isImageBySuffix(): boolean {
    return this.isGif || this.isWebp
  }

  get isMp3(): boolean {
    return this.filename.endsWith('.mp3')
  }

  get isWav(): boolean {
    return this.filename.endsWith('.wav')
  }

  get isOgg(): boolean {
    return this.filename.endsWith('.ogg')
  }

  get isFlac(): boolean {
    return this.filename.endsWith('.flac')
  }

  get isAudioBySuffix(): boolean {
    return this.isMp3 || this.isWav || this.isOgg || this.isFlac
  }

  get isVideo(): boolean {
    const isVideoByType =
      this.mediaType === 'video' || !!this.format?.startsWith('video/')
    return (
      this.isVideoBySuffix ||
      (isVideoByType && !this.isImageBySuffix && !this.isAudioBySuffix)
    )
  }

  get isImage(): boolean {
    return (
      this.isImageBySuffix ||
      (this.mediaType === 'images' &&
        !this.isVideoBySuffix &&
        !this.isAudioBySuffix)
    )
  }

  get isAudio(): boolean {
    const isAudioByType =
      this.mediaType === 'audio' || !!this.format?.startsWith('audio/')
    return (
      this.isAudioBySuffix ||
      (isAudioByType && !this.isImageBySuffix && !this.isVideoBySuffix)
    )
  }

  get supportsPreview(): boolean {
    return this.isImage || this.isVideo || this.isAudio
  }
}

export class TaskItemImpl {
  readonly taskType: TaskType
  readonly prompt: TaskPrompt
  readonly status?: TaskStatus
  readonly outputs: TaskOutput
  readonly image?: any[] // 从 generated_images 对应的 uploaded_files 数据
  readonly flatOutputs: ReadonlyArray<ResultItemImpl>

  constructor(
    taskType: TaskType,
    prompt: TaskPrompt,
    status?: TaskStatus,
    outputs?: TaskOutput,
    flatOutputs?: ReadonlyArray<ResultItemImpl>,
    image?: any[]
  ) {
    this.taskType = taskType
    this.prompt = prompt
    this.status = status
    this.image = image
    // Remove animated outputs from the outputs object
    // outputs.animated is an array of boolean values that indicates if the images
    // array in the result are animated or not.
    // The queueStore does not use this information.
    // It is part of the legacy API response. We should redesign the backend API.
    // https://github.com/Comfy-Org/ComfyUI_frontend/issues/2739
    this.outputs = _.mapValues(outputs ?? {}, (nodeOutputs) =>
      _.omit(nodeOutputs, 'animated')
    )
    this.flatOutputs = flatOutputs ?? this.calculateFlatOutputs()
  }

  calculateFlatOutputs(): ReadonlyArray<ResultItemImpl> {
    // 如果有 image 字段，优先使用它（从 generated_images 对应的 uploaded_files 获取）
    if (this.image && this.image.length > 0) {
      return this.image.map((file: any) => {
        // 根据 mime_type 判断媒体类型
        let mediaType = 'images' // 默认
        if (file.mime_type) {
          if (file.mime_type.startsWith('video/')) {
            mediaType = 'video'
          } else if (file.mime_type.startsWith('audio/')) {
            mediaType = 'audio'
          } else if (file.mime_type.startsWith('image/')) {
            mediaType = 'images'
          }
        } else {
          // 根据文件扩展名判断
          const filename = file.filename || file.original_filename || ''
          if (filename.match(/\.(mp4|webm|avi|mov)$/i)) {
            mediaType = 'video'
          } else if (filename.match(/\.(mp3|wav|ogg|flac)$/i)) {
            mediaType = 'audio'
          }
        }

        // 创建 ResultItemImpl
        // 优先使用 obs_url（完整的 URL），如果没有则使用其他路径
        const fileUrl =
          file.obs_url ||
          file.local_path ||
          file.filename ||
          file.original_filename ||
          ''

        const resultItem: any = {
          filename: fileUrl,
          subfolder: file.subfolder || '',
          type: 'output',
          nodeId: 'output', // 使用默认 nodeId
          mediaType,
          // 保存原始 uploaded_file 数据，以便后续使用
          _uploadedFile: file
        }

        // 如果有视频相关字段，添加它们
        if (file.duration) {
          resultItem.duration = file.duration
        }
        if (file.format) {
          resultItem.format = file.format
        }

        return new ResultItemImpl(resultItem)
      })
    }

    // 如果没有 image 字段，回退到使用 outputs
    if (!this.outputs) {
      return []
    }
    return Object.entries(this.outputs).flatMap(([nodeId, nodeOutputs]) =>
      Object.entries(nodeOutputs).flatMap(([mediaType, items]) =>
        (items as ResultItem[]).map(
          (item: ResultItem) =>
            new ResultItemImpl({
              ...item,
              nodeId,
              mediaType
            })
        )
      )
    )
  }

  get previewOutput(): ResultItemImpl | undefined {
    return (
      this.flatOutputs.find(
        // Prefer saved media files over the temp previews
        (output) => output.type === 'output' && output.supportsPreview
      ) ?? this.flatOutputs.find((output) => output.supportsPreview)
    )
  }

  get apiTaskType(): APITaskType {
    switch (this.taskType) {
      case 'Running':
      case 'Pending':
        return 'queue'
      case 'History':
        return 'history'
    }
  }

  get key() {
    return this.promptId + this.displayStatus
  }

  get queueIndex() {
    return this.prompt[0]
  }

  get promptId() {
    return this.prompt[1]
  }

  get promptInputs() {
    return this.prompt[2]
  }

  get extraData() {
    return this.prompt[3]
  }

  get outputsToExecute() {
    return this.prompt[4]
  }

  get extraPngInfo() {
    return this.extraData.extra_pnginfo
  }

  get clientId() {
    return this.extraData.client_id
  }

  get workflow(): ComfyWorkflowJSON | undefined {
    return this.extraPngInfo?.workflow
  }

  get messages() {
    return this.status?.messages || []
  }

  get interrupted() {
    return _.some(
      this.messages,
      (message) => message[0] === 'execution_interrupted'
    )
  }

  get isHistory() {
    return this.taskType === 'History'
  }

  get isRunning() {
    return this.taskType === 'Running'
  }

  get displayStatus(): TaskItemDisplayStatus {
    switch (this.taskType) {
      case 'Running':
        return TaskItemDisplayStatus.Running
      case 'Pending':
        return TaskItemDisplayStatus.Pending
      case 'History':
        if (this.interrupted) return TaskItemDisplayStatus.Cancelled

        switch (this.status!.status_str) {
          case 'success':
            return TaskItemDisplayStatus.Completed
          case 'error':
            return TaskItemDisplayStatus.Failed
        }
    }
  }

  get executionStartTimestamp() {
    const message = this.messages.find(
      (message) => message[0] === 'execution_start'
    )
    return message ? message[1].timestamp : undefined
  }

  get executionEndTimestamp() {
    const messages = this.messages.filter((message) =>
      [
        'execution_success',
        'execution_interrupted',
        'execution_error'
      ].includes(message[0])
    )
    if (!messages.length) {
      return undefined
    }
    return _.max(messages.map((message) => message[1].timestamp))
  }

  get executionTime() {
    if (!this.executionStartTimestamp || !this.executionEndTimestamp) {
      return undefined
    }
    return this.executionEndTimestamp - this.executionStartTimestamp
  }

  get executionTimeInSeconds() {
    return this.executionTime !== undefined
      ? this.executionTime / 1000
      : undefined
  }

  public async loadWorkflow(app: ComfyApp) {
    if (!this.workflow) {
      return
    }
    await app.loadGraphData(toRaw(this.workflow))
    if (this.outputs) {
      const nodeOutputsStore = useNodeOutputStore()
      const rawOutputs = toRaw(this.outputs)
      for (const nodeExecutionId in rawOutputs) {
        nodeOutputsStore.setNodeOutputsByExecutionId(
          nodeExecutionId,
          rawOutputs[nodeExecutionId]
        )
      }
      useExtensionService().invokeExtensions(
        'onNodeOutputsUpdated',
        app.nodeOutputs
      )
    }
  }

  public flatten(): TaskItemImpl[] {
    if (this.displayStatus !== TaskItemDisplayStatus.Completed) {
      return [this]
    }

    return this.flatOutputs.map(
      (output: ResultItemImpl, i: number) =>
        new TaskItemImpl(
          this.taskType,
          [
            this.queueIndex,
            `${this.promptId}-${i}`,
            this.promptInputs,
            this.extraData,
            this.outputsToExecute
          ],
          this.status,
          {
            [output.nodeId]: {
              [output.mediaType]: [output]
            }
          },
          [output]
        )
    )
  }
}

export const useQueueStore = defineStore('queue', () => {
  const runningTasks = ref<TaskItemImpl[]>([])
  const pendingTasks = ref<TaskItemImpl[]>([])
  const historyTasks = ref<TaskItemImpl[]>([])
  const maxHistoryItems = ref(64)
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  const pageSize = ref(20) // 每页数量
  const currentPage = ref(1) // 当前页码
  const totalPages = ref(0) // 总页数
  const totalItems = ref(0) // 总记录数
  const hasMoreHistory = ref(true) // 是否还有更多历史记录
  const hasInitialized = ref(false) // 是否已经初始化过

  const tasks = computed<TaskItemImpl[]>(
    () =>
      [
        ...pendingTasks.value,
        ...runningTasks.value,
        ...historyTasks.value
      ] as TaskItemImpl[]
  )

  const flatTasks = computed<TaskItemImpl[]>(() =>
    tasks.value.flatMap((task: TaskItemImpl) => task.flatten())
  )

  const lastHistoryQueueIndex = computed<number>(() =>
    historyTasks.value.length ? historyTasks.value[0].queueIndex : -1
  )

  const hasPendingTasks = computed<boolean>(() => pendingTasks.value.length > 0)

  const update = async (isInitialLoad?: boolean) => {
    // 如果正在加载中，避免重复请求
    if (isLoading.value) {
      return
    }

    // 精简模式 /workflow2 未登录时跳过，避免 401 反复请求
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/workflow2')
    ) {
      const authStore = useAuthStore()
      if (authStore.isInitialized && !authStore.isAuthenticated) {
        return
      }
    }

    isLoading.value = true
    try {
      // 如果没有明确指定，且还没有初始化过，则使用初始加载模式
      const shouldInitialLoad =
        isInitialLoad ??
        (!hasInitialized.value && historyTasks.value.length === 0)

      // 如果是初始加载，重置分页状态
      if (shouldInitialLoad) {
        currentPage.value = 1
      }

      const [queue, history] = await Promise.all([
        api.getQueue(),
        api.getHistory(
          undefined, // max_items (不再使用)
          undefined, // offset (不再使用)
          true, // exclude_no_output
          currentPage.value, // page
          pageSize.value // page_size
        )
      ])

      const toClassAll = (tasks: TaskItem[]): TaskItemImpl[] =>
        tasks
          .map(
            (task: TaskItem) =>
              new TaskItemImpl(
                task.taskType,
                task.prompt,
                // status and outputs only exist on history tasks
                'status' in task ? task.status : undefined,
                'outputs' in task ? task.outputs : undefined,
                undefined, // flatOutputs will be calculated
                // image field only exists on history tasks
                'image' in task && task.image ? task.image : undefined
              )
          )
          .sort((a, b) => b.queueIndex - a.queueIndex)

      runningTasks.value = toClassAll(queue.Running)
      pendingTasks.value = toClassAll(queue.Pending)

      // 更新分页信息
      if (history.pagination) {
        currentPage.value = history.pagination.page
        totalPages.value = history.pagination.total_pages
        totalItems.value = history.pagination.total
        hasMoreHistory.value = history.pagination.has_more
      }

      if (shouldInitialLoad) {
        // 初始加载时，替换所有历史记录
        historyTasks.value = toClassAll(history.History).sort(
          (a, b) => b.queueIndex - a.queueIndex
        )
        hasInitialized.value = true
      } else {
        // 非初始加载，替换所有历史记录（分页模式）
        historyTasks.value = toClassAll(history.History).sort(
          (a, b) => b.queueIndex - a.queueIndex
        )
        hasInitialized.value = true
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 轮询更新（用于生图时的自动刷新）
   * 始终请求第一页以获取最新数据，但不影响当前查看的页码
   */
  const poll = async () => {
    // 如果正在加载中，避免重复请求
    if (isLoading.value) {
      return
    }

    // 精简模式 /workflow2 未登录时跳过，避免 401 反复请求
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/workflow2')
    ) {
      const authStore = useAuthStore()
      if (authStore.isInitialized && !authStore.isAuthenticated) {
        return
      }
    }

    isLoading.value = true
    try {
      const [queue, history] = await Promise.all([
        api.getQueue(),
        api.getHistory(
          undefined, // max_items (不再使用)
          undefined, // offset (不再使用)
          true, // exclude_no_output
          1, // 轮询时始终使用第一页
          pageSize.value // page_size
        )
      ])

      const toClassAll = (tasks: TaskItem[]): TaskItemImpl[] =>
        tasks
          .map(
            (task: TaskItem) =>
              new TaskItemImpl(
                task.taskType,
                task.prompt,
                // status and outputs only exist on history tasks
                'status' in task ? task.status : undefined,
                'outputs' in task ? task.outputs : undefined,
                undefined, // flatOutputs will be calculated
                // image field only exists on history tasks
                'image' in task && task.image ? task.image : undefined
              )
          )
          .sort((a, b) => b.queueIndex - a.queueIndex)

      runningTasks.value = toClassAll(queue.Running)
      pendingTasks.value = toClassAll(queue.Pending)

      // 更新总数信息（但不改变当前页码）
      if (history.pagination) {
        totalPages.value = history.pagination.total_pages
        totalItems.value = history.pagination.total
        // 轮询时不更新 currentPage，保持用户当前查看的页码
        // 但更新 hasMoreHistory，以便用户知道是否还有更多
        if (currentPage.value === 1) {
          // 如果用户正在查看第一页，可以更新 hasMoreHistory
          hasMoreHistory.value = history.pagination.has_more
        }
      }

      // 轮询时，将第一页的新数据合并到历史记录列表顶部（去重）
      const newHistoryItems = toClassAll(history.History)
      if (newHistoryItems.length > 0) {
        const existingPromptIds = new Set(
          historyTasks.value.map((item) => item.promptId)
        )
        const uniqueNewItems = newHistoryItems.filter(
          (item) => !existingPromptIds.has(item.promptId)
        )

        // 将新数据合并到列表顶部，保持排序
        if (uniqueNewItems.length > 0) {
          historyTasks.value = [...uniqueNewItems, ...historyTasks.value].sort(
            (a, b) => b.queueIndex - a.queueIndex
          )
        }
      }
    } finally {
      isLoading.value = false
    }
  }

  const loadMoreHistory = async () => {
    if (isLoadingMore.value || !hasMoreHistory.value) {
      return
    }

    isLoadingMore.value = true
    try {
      // 加载下一页
      const nextPage = currentPage.value + 1

      // 加载更多历史记录（使用分页参数）
      const history = await api.getHistory(
        undefined, // max_items (不再使用)
        undefined, // offset (不再使用)
        true, // exclude_no_output
        nextPage, // page
        pageSize.value // page_size
      )

      const toClassAll = (tasks: TaskItem[]): TaskItemImpl[] =>
        tasks
          .map(
            (task: TaskItem) =>
              new TaskItemImpl(
                task.taskType,
                task.prompt,
                'status' in task ? task.status : undefined,
                'outputs' in task ? task.outputs : undefined,
                undefined, // flatOutputs will be calculated
                // image field only exists on history tasks
                'image' in task && task.image ? task.image : undefined
              )
          )
          .sort((a, b) => b.queueIndex - a.queueIndex)

      // 更新分页信息
      if (history.pagination) {
        currentPage.value = history.pagination.page
        totalPages.value = history.pagination.total_pages
        totalItems.value = history.pagination.total
        hasMoreHistory.value = history.pagination.has_more
      } else {
        // 如果没有分页信息，根据返回的数据量判断
        hasMoreHistory.value = history.History.length >= pageSize.value
      }

      const newHistoryItems = toClassAll(history.History)

      if (newHistoryItems.length > 0) {
        // 合并新加载的记录，去重
        const existingPromptIds = new Set(
          historyTasks.value.map((item) => item.promptId)
        )
        const uniqueNewItems = newHistoryItems.filter(
          (item) => !existingPromptIds.has(item.promptId)
        )

        historyTasks.value = [...historyTasks.value, ...uniqueNewItems].sort(
          (a, b) => b.queueIndex - a.queueIndex
        )
      } else {
        // 没有新记录，说明没有更多了
        hasMoreHistory.value = false
      }
    } catch (error) {
      console.error('加载更多历史记录失败:', error)
      hasMoreHistory.value = false
    } finally {
      isLoadingMore.value = false
    }
  }

  const clear = async (
    targets: ('queue' | 'history')[] = ['queue', 'history']
  ) => {
    if (targets.length === 0) {
      return
    }
    await Promise.all(targets.map((type) => api.clearItems(type)))
    await update()
  }

  const deleteTask = async (task: TaskItemImpl) => {
    await api.deleteItem(task.apiTaskType, task.promptId)
    await update()
  }

  return {
    runningTasks,
    pendingTasks,
    historyTasks,
    maxHistoryItems,
    isLoading,
    isLoadingMore,
    hasMoreHistory,
    pageSize,
    currentPage,
    totalPages,
    totalItems,

    tasks,
    flatTasks,
    lastHistoryQueueIndex,
    hasPendingTasks,

    update,
    poll,
    loadMoreHistory,
    clear,
    delete: deleteTask
  }
})

export const useQueuePendingTaskCountStore = defineStore(
  'queuePendingTaskCount',
  {
    state: () => ({
      count: 0
    }),
    actions: {
      update(e: CustomEvent<StatusWsMessageStatus>) {
        console.log(
          'useQueuePendingTaskCountStore update called with:',
          e.detail
        )
        this.count = e.detail?.exec_info?.queue_remaining || 0
        console.log('Updated queue count to:', this.count)
      }
    }
  }
)

export type AutoQueueMode = 'disabled' | 'instant' | 'change'

export const useQueueSettingsStore = defineStore('queueSettingsStore', {
  state: () => ({
    mode: 'disabled' as AutoQueueMode,
    batchCount: 1
  })
})
